import { useEffect, useState } from "react"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { ListItemButton, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons"
import { HardwarePackagesResponse, ShopsQuery, hardwarePackagesList } from "../../../shopsApi"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { CustomAccordion, CustomAccordionContent } from "../CustomCheckboxAccordion"
import { CreateOrEditShopFormValues, ProductOrderLine } from "../../../shopsTypes"
import { useExternalLinks } from "../../../../../../../shared/hooks/useAppLinks"
import { useMerchant } from "../../../../../../merchants/merchantHooks"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { ProductContent } from "./ProductContent"
import { InfoBox } from "../InfoBox"

const useStyles = makeStyles()((theme) => ({
    button: {
        flex: "none",
        gap: "0.5rem",
        color: "#2196f3",
    },
    lightColor: {
        color: theme.palette.text.secondary,
    },
}))

const selectShopsHardwarePackage = (data: HardwarePackagesResponse) => {
    const shopHardware = data.shopHardware
    if (!shopHardware) return []
    return shopHardware.productGroups
}

export const SelectHardwarePackages = () => {
    const { merchantId } = useMerchant()
    const { classes } = useStyles()
    const { values, setFieldValue } = useFormikContext<CreateOrEditShopFormValues>()
    const [selectedPackages, setSelectedPackages] = useState<ProductOrderLine[]>([])

    const { data: allPackages = [] } = useApiQuery(hardwarePackagesList, {
        queryName: ShopsQuery.HARDWARE_PACKAGES,
        dependencies: {
            merchantId,
            shopType: values.type,
        },
        select: selectShopsHardwarePackage,
    })

    useEffect(() => {
        setFieldValue("hardwarePackage", selectedPackages)
    }, [selectedPackages])

    const handleExternalLink = useExternalLinks()

    return (
        <>
            <LocalizedStrict id="merchant-shop-form-hardware-packages">
                <Typography variant="subtitle1" fontWeight={700}>
                    Select hardware packages
                </Typography>
            </LocalizedStrict>
            <LocalizedStrict id="merchant-shop-form-hardware-packages-info" attrs={{ description: true }}>
                <InfoBox description="Selecting products below will not result in a purchase. You will subsequently receive an offer for hardware, which you can approve." />
            </LocalizedStrict>
            {allPackages.map((group, groupIndex) => (
                <CustomAccordion
                    key={groupIndex}
                    title={
                        <ListItemButton onClick={() => handleExternalLink(group.helpLink)} className={classes.button}>
                            <Typography>{group.name}</Typography>
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className={classes.lightColor} />
                        </ListItemButton>
                    }
                >
                    {group.products.map((product, productIndex) => (
                        <CustomAccordionContent
                            key={productIndex}
                            content={
                                <ProductContent
                                    product={product}
                                    selectedPackages={selectedPackages}
                                    setSelectedPackages={setSelectedPackages}
                                />
                            }
                            imageUrl={product.cardDetails.imageUrl}
                        />
                    ))}
                </CustomAccordion>
            ))}
        </>
    )
}
